import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Button } from "react-bootstrap";
import Swal from "sweetalert2";

import "./style.scss";
import { ClinicApi } from "../../api";
import { ClinicPatientType } from "../../DTO";
import { Loading } from "../../components";
import { useAppLocation, useAppNavigate, useAppParams } from "../../Router/hook";
import { useAppSelector } from "../../redux-config/hooks";
import { selectUser } from "../../redux-config/entities/user";
import { getClinicDisplayName } from "../../utils";

const scoreValue = (value: any) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    const numeric = Number(value);

    return Number.isFinite(numeric) ? String(Math.round(numeric)) : String(value);
};

const getMonthlyData = (patient?: ClinicPatientType) => {
    if (!patient) {
        return null;
    }

    return (
        patient.progressPreview?.latestMonthly ||
        patient.latestMonthlySummary ||
        patient.raw?.progress_preview?.latest_monthly ||
        patient.raw?.latest_monthly_summary ||
        null
    );
};

const trendArrow = (direction?: string | null) => {
    if (direction === "improving") {
        return "↑";
    }

    if (direction === "declining") {
        return "↓";
    }

    return "→";
};

const getRiskLabel = (patient: ClinicPatientType, monthly: any) => {
    if (patient.clinicalReportSummary?.riskLabel) {
        return patient.clinicalReportSummary.riskLabel;
    }

    const trend = monthly?.trendDirection || monthly?.trend?.direction;
    const redDays =
        monthly?.redDaysCount ?? monthly?.traffic_light_distribution?.red_days ?? 0;

    if (patient.condition === "CM Risk" || trend === "declining" || redDays >= 2) {
        return "High Risk - Escalate";
    }

    if (patient.condition === "T2D") {
        return "At Risk - Monitor";
    }

    return "Stable - Maintain";
};

const getRecommendedActions = (risk: string, patient: ClinicPatientType) => {
    if (patient.clinicalReportSummary?.recommendedActions?.length) {
        return patient.clinicalReportSummary.recommendedActions;
    }

    if (risk.startsWith("High")) {
        return [
            "Review therapy and recent biometric trend",
            "Improve activity consistency",
            "Stabilize meal patterns",
        ];
    }

    if (patient.therapy === "insulin" || patient.therapy === "GLP-1") {
        return [
            "Continue medication follow-up",
            "Monitor lifestyle adherence",
            "Maintain sleep and stress routine",
        ];
    }

    return [
        "Continue current care plan",
        "Maintain lifestyle engagement",
        "Review again next reporting period",
    ];
};

const getPatientAgeSex = (patient: ClinicPatientType) => {
    const summary = patient.clinicalReportSummary;

    if (summary?.age && summary?.sex) {
        return `${summary.age} y/o ${summary.sex}`;
    }

    return "-";
};

const getConditionLabel = (patient: ClinicPatientType) =>
    patient.clinicalReportSummary?.conditionLabel ||
    patient.condition ||
    patient.patientProfile?.condition ||
    "-";

const getClinicName = (patient: ClinicPatientType) =>
    patient.clinic?.name || patient.raw?.clinic?.name || "Kelowna PCN";

const getDriverLight = (light?: string | null, score?: any) => {
    if (light === "green" || light === "amber" || light === "red") {
        return light;
    }

    const numeric = Number(score);

    if (!Number.isFinite(numeric)) {
        return "amber";
    }

    if (numeric >= 75) {
        return "green";
    }

    if (numeric >= 60) {
        return "amber";
    }

    return "red";
};

const getDriverMeta = (monthly: any, key: string, fallbackKey?: string) => {
    const category =
        monthly?.categoryAverages?.[key] ||
        monthly?.category_averages?.[key] ||
        (fallbackKey
            ? monthly?.categoryAverages?.[fallbackKey] ||
              monthly?.category_averages?.[fallbackKey]
            : null);
    const score = category?.avgScore ?? category?.avg_score ?? null;

    return {
        score,
        light: getDriverLight(category?.light, score),
    };
};

export const ClinicPatientDetails = () => {
    const user = useAppSelector(selectUser);
    const navigate = useAppNavigate();
    const location = useAppLocation();
    const { id } = useAppParams();

    const [loading, setLoading] = useState(false);
    const [patient, setPatient] = useState<ClinicPatientType>();

    useEffect(() => {
        const state = location.state as { successMessage?: string } | null;

        if (state?.successMessage) {
            Swal.fire({
                icon: "success",
                title: "Referral created successfully",
                text: state.successMessage,
            });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.pathname, location.state, navigate]);

    useEffect(() => {
        const fetchPatient = async () => {
            try {
                setLoading(true);
                const data = await ClinicApi.ShowClinicPatient(user, Number(id));
                setPatient(data.patient);
            } catch (err: any) {
                Swal.fire({
                    icon: "error",
                    text:
                        err?.data?.message ||
                        err?.message ||
                        "Unable to load clinic patient details.",
                }).then(() => {
                    navigate("/clinic/patients");
                });
            } finally {
                setLoading(false);
            }
        };

        fetchPatient();
    }, [id, navigate, user]);

    const monthly = getMonthlyData(patient);
    const risk = patient ? getRiskLabel(patient, monthly) : "";
    const trend = monthly?.trendDirection || monthly?.trend?.direction || null;
    const fallbackReportPeriod = useMemo(
        () =>
            new Intl.DateTimeFormat("en", {
                month: "short",
                year: "numeric",
            }).format(new Date()),
        [],
    );
    const reportPeriod =
        patient?.clinicalReportSummary?.reportingPeriod || fallbackReportPeriod;
    const monthsOnD2type = patient?.clinicalReportSummary?.monthsOnD2type ?? "-";
    const clinicalRows = patient?.clinicalReportSummary?.clinicalSummary || [];
    const userInfo = patient?.userInformation?.[0];
    const aiInsights = patient?.clinicalReportSummary?.aiInsights || [];
    const lifestyleRows: Array<[
        string,
        ReturnType<typeof getDriverMeta>,
    ]> = [
        ["Nutrition", getDriverMeta(monthly, "nutrition", "carbs")],
        ["Meal Pattern", getDriverMeta(monthly, "meal_pattern", "sugar")],
        ["Activity", getDriverMeta(monthly, "activity")],
        ["Sleep", getDriverMeta(monthly, "sleep")],
        ["Mindset", getDriverMeta(monthly, "mindset", "stress")],
    ];

    return (
        <div className="my-4 clinic-page clinic-detail-page">
            <Helmet>
                <title>D2Type | Patient Detail</title>
            </Helmet>

            {loading && <Loading />}

            {patient && (
                <div className="clinic-detail-shell">
                    <div className="clinic-detail-header">
                        <div>
                            <h2 className="clinic-snapshot-title">
                                Patient Detail | {getClinicDisplayName(patient)}
                            </h2>
                            <p className="clinic-snapshot-subtitle">
                                Monthly synopsis and patient-level follow-up
                            </p>
                        </div>
                        <div className="clinic-detail-actions">
                            <div className="clinic-detail-clinic-name" aria-disabled="true">
                                {getClinicName(patient)}
                            </div>
                            <Button variant="outline-primary" size="sm">
                                Live view
                            </Button>
                        </div>
                    </div>

                    <div className="clinic-detail-patient-line">
                        <strong>
                            {getClinicDisplayName(patient)} | {getPatientAgeSex(patient)} |{" "}
                            {getConditionLabel(patient)} | {risk}
                        </strong>
                        <span>
                            Reporting period: {reportPeriod} &nbsp; | &nbsp; Months on D2Type:{" "}
                            {monthsOnD2type}
                        </span>
                    </div>

                    <div className="clinic-detail-grid">
                        <section className="clinic-detail-card">
                            <h3>Clinical Summary</h3>
                            {(clinicalRows.length
                                ? clinicalRows
                                : [
                                      {
                                          label: "Weight",
                                          value: `${userInfo?.weight || "-"} ${
                                              userInfo?.weightUnit || ""
                                          }`.trim(),
                                      },
                                      { label: "Condition", value: patient.condition || "-" },
                                      { label: "Therapy", value: patient.therapy || "-" },
                                  ]
                            ).map((row) => (
                                <p key={row.label}>
                                    {row.label}: {row.value}
                                </p>
                            ))}
                        </section>

                        <section className="clinic-detail-card">
                            <h3>Lifestyle Drivers (Score &amp; Trend vs last month)</h3>
                            <div className="clinic-driver-list">
                                {lifestyleRows.map(([label, meta]) => (
                                    <div className="clinic-driver-row" key={label}>
                                        <span
                                            className={`clinic-driver-dot clinic-driver-${
                                                meta.light
                                            }`}
                                        />
                                        <span>{label}</span>
                                        <strong>
                                            {scoreValue(meta.score)}/100 {trendArrow(trend)}
                                        </strong>
                                    </div>
                                ))}
                            </div>
                            <div className="clinic-detail-legend">
                                ↑ Improving&nbsp;&nbsp; → Stable&nbsp;&nbsp; ↓ Worsening
                            </div>
                        </section>

                        <section className="clinic-detail-card">
                            <h3>Clinical Insight</h3>
                            {(aiInsights.length
                                ? aiInsights
                                : [
                                      `Primary driver: ${
                                          monthly?.worstCategory ||
                                          monthly?.worst_category ||
                                          monthly?.insights?.worst_category ||
                                          "activity"
                                      }`,
                                      monthly?.monthlyInsightPreview ||
                                          monthly?.monthlyInsight ||
                                          monthly?.insights?.coach_summary_preview ||
                                          monthly?.insights?.monthly_insight ||
                                          "Risk and lifestyle trend will be clearer after more logged data.",
                                  ]
                            ).map((line) => (
                                <p key={line}>{line}</p>
                            ))}
                        </section>

                        <section className="clinic-detail-card">
                            <h3>Recommended Actions</h3>
                            {getRecommendedActions(risk, patient).map((action) => (
                                <p key={action}>{action}</p>
                            ))}
                        </section>
                    </div>
                </div>
            )}
        </div>
    );
};
